import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search, Plus, Clock, GraduationCap, Eye, CheckSquare, MessageSquare, Users,
  ChevronRight, Edit, X
} from 'lucide-react';
import ModuleMediaManager from '@/components/admin/ModuleMediaManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SequenceCard = ({ seq }) => {
  const hasImage = seq.image_url && seq.image_url.startsWith('http');
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer" data-testid={`seq-card-${seq.seq_id || seq.module_id}`}>
      <div className="h-36 bg-violet-500 flex items-center justify-center relative overflow-hidden">
        {hasImage ? (
          <img src={seq.image_url} alt={seq.title} className="w-full h-full object-cover" />
        ) : (
          <GraduationCap className="h-14 w-14 text-white/80" />
        )}
      </div>
      <div className="p-3 space-y-2">
        <h3 className="font-bold text-xs text-slate-900 line-clamp-2 min-h-[2rem]">{seq.title}</h3>
        {seq.duration_minutes > 0 && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="h-3 w-3" />{seq.duration_minutes}'
          </div>
        )}
        <div className="flex items-center gap-2 text-slate-400">
          {seq.show_score !== false && <Eye className="h-3.5 w-3.5" />}
          {seq.activities?.length > 0 && <CheckSquare className="h-3.5 w-3.5" />}
          {seq.forum_enabled && <MessageSquare className="h-3.5 w-3.5" />}
          {seq.visibility === 'group' && <Users className="h-3.5 w-3.5" />}
        </div>
      </div>
    </div>
  );
};

const ElearningSequencesPage = () => {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeTab, setActiveTab] = useState('sequences');
  const [mediaModule, setMediaModule] = useState(null);

  const fetchSequences = useCallback(async () => {
    try {
      // Fetch e-learning sequences from all sessions
      const sessRes = await fetch(`${API}/admin/sessions`, { credentials: 'include' });
      if (!sessRes.ok) return;
      const sessions = await sessRes.json();
      const allSeqs = [];
      for (const sess of sessions) {
        const seqRes = await fetch(`${API}/admin/sessions/${sess.session_id}/elearning/sequences`, { credentials: 'include' });
        if (seqRes.ok) {
          const data = await seqRes.json();
          allSeqs.push(...data.map(s => ({ ...s, session_title: sess.title, session_id: sess.session_id })));
        }
      }
      // Also fetch course modules as sequences
      const coursesRes = await fetch(`${API}/courses/?published_only=false`, { credentials: 'include' });
      if (coursesRes.ok) {
        const courses = await coursesRes.json();
        for (const course of courses) {
          const modRes = await fetch(`${API}/courses/${course.course_id}/modules`, { credentials: 'include' });
          if (modRes.ok) {
            const modules = await modRes.json();
            modules.forEach(mod => {
              allSeqs.push({
                seq_id: mod.module_id,
                title: mod.title,
                description: mod.description,
                image_url: mod.video_url || '',
                duration_minutes: mod.duration_minutes || 0,
                activities: mod.resources || [],
                show_score: true,
                forum_enabled: false,
                course_title: course.title,
                course_category: course.category,
                is_published: course.is_published
              });
            });
          }
        }
      }
      setSequences(allSeqs);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSequences(); }, [fetchSequences]);

  const refreshMedia = async () => {
    if (!mediaModule) return;
    try {
      const res = await fetch(`${API}/courses/${mediaModule.courseId}/modules/${mediaModule.moduleId}/media`, { credentials: 'include' });
      const media = res.ok ? await res.json() : [];
      setMediaModule(prev => ({ ...prev, media }));
      fetchSequences();
    } catch (e) {}
  };

  const categories = [...new Set(sequences.map(s => s.course_category).filter(Boolean))];

  const filtered = sequences.filter(s => {
    const matchSearch = !searchQuery || s.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = !categoryFilter || s.course_category === categoryFilter;
    return matchSearch && matchCategory;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="elearning-sequences-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>LMS</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-violet-600 font-medium">Séquences e-learning</span>
        </div>

        <h1 className="text-3xl font-black text-slate-900">Bibliothèque de séquences e-learning</h1>

        {/* Tabs */}
        <div className="flex border-b border-slate-200" data-testid="sequence-tabs">
          <button
            className={`px-8 py-3 text-sm font-medium transition-colors ${activeTab === 'sequences' ? 'text-violet-600 border-b-2 border-violet-500 bg-violet-50/50' : 'text-slate-500 hover:text-slate-700'}`}
            onClick={() => setActiveTab('sequences')}
            data-testid="tab-sequences"
          >
            Séquences e-learning
          </button>
          <button
            className={`px-8 py-3 text-sm font-medium transition-colors ${activeTab === 'categories' ? 'text-violet-600 border-b-2 border-violet-500 bg-violet-50/50' : 'text-slate-500 hover:text-slate-700'}`}
            onClick={() => setActiveTab('categories')}
            data-testid="tab-categories"
          >
            Catégories
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <Button className="bg-violet-500 hover:bg-violet-600 text-white rounded-lg gap-2" data-testid="btn-create-sequence">
            <Plus className="h-4 w-4" />Créer une séquence
          </Button>
          <Button variant="outline" className="gap-2 text-slate-600" data-testid="btn-mass-edit">
            <Edit className="h-4 w-4" />Modifier en masse
          </Button>
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-sequences"
            />
          </div>
          <select
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 bg-white"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="filter-category"
          >
            <option value="">Catégorie de séquence</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {(searchQuery || categoryFilter) && (
            <button
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
              onClick={clearFilters}
              data-testid="btn-clear-filters"
            >
              <X className="h-3.5 w-3.5" />Effacer tout
            </button>
          )}
        </div>

        {/* Content */}
        {activeTab === 'sequences' ? (
          <>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="h-52 bg-slate-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="border border-slate-200 rounded-xl p-12 text-center">
                <GraduationCap className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune séquence trouvée</h3>
                <p className="text-sm text-slate-500">Créez des séquences pour enrichir votre bibliothèque e-learning.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {filtered.map((seq, idx) => (
                  <SequenceCard key={seq.seq_id || seq.module_id || idx} seq={seq} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="border border-slate-200 rounded-xl p-12 text-center">
            <div className="text-slate-400 text-sm">
              {categories.length === 0 ? (
                <p>Aucune catégorie définie. Les catégories apparaissent automatiquement à partir de vos parcours.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {categories.map(c => (
                    <button
                      key={c}
                      className="bg-violet-50 text-violet-700 px-4 py-3 rounded-lg text-sm font-medium hover:bg-violet-100 transition-colors"
                      onClick={() => { setCategoryFilter(c); setActiveTab('sequences'); }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {mediaModule && (
        <ModuleMediaManager
          open={!!mediaModule}
          onOpenChange={(open) => !open && setMediaModule(null)}
          courseId={mediaModule.courseId}
          moduleId={mediaModule.moduleId}
          moduleName={mediaModule.moduleName}
          mediaList={mediaModule.media}
          onMediaChange={refreshMedia}
        />
      )}
    </Layout>
  );
};

export default ElearningSequencesPage;
